import { z } from 'zod';

import type { LabComponentLoader, LabFeatureFlagKey } from './types';

const LAB_ID_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const LAB_FLAG_PATTERN = /^labs-[a-z0-9-]+-allowed$/;

/** Lifecycle of a lab: `internal` for staff, `beta` for opted-in customers, `sunset` once it is retired. */
export const LAB_STATUS = {
  INTERNAL: 'internal',
  BETA: 'beta',
  SUNSET: 'sunset'
} as const;

const isComponentLoader = (value: unknown): value is LabComponentLoader => typeof value === 'function';
const isLabFeatureFlagKey = (value: unknown): value is LabFeatureFlagKey =>
  typeof value === 'string' && LAB_FLAG_PATTERN.test(value);

/** Validates a lab manifest. `owner`, `flag` and `sunset` are required so a lab cannot ship anonymous or forever. */
export const labManifestSchema = z
  .object({
    id: z.string().regex(LAB_ID_PATTERN, 'id must be kebab-case'),
    title: z.string().min(1),
    description: z.string().min(1),
    owner: z.string().min(1),
    flag: z.custom<LabFeatureFlagKey>(isLabFeatureFlagKey, 'flag must be labs-<id>-allowed'),
    status: z.nativeEnum(LAB_STATUS),
    sunset: z.string().date('sunset must be an ISO date (YYYY-MM-DD)'),
    scope: z.literal('location'),
    component: z.custom<LabComponentLoader>(isComponentLoader, 'component must be a dynamic import')
  })
  .refine((manifest) => manifest.flag === `labs-${manifest.id}-allowed`, {
    message: 'flag must be labs-<id>-allowed',
    path: ['flag']
  });

/** A validated lab manifest. */
export type LabManifest = z.infer<typeof labManifestSchema>;

/** One of the `LAB_STATUS` values. */
export type LabStatus = LabManifest['status'];

/**
 * Declares a lab. Throws when the manifest is invalid, so a lab with a missing owner or sunset date
 * fails the host's registry spec instead of reaching users.
 */
export const defineLab = (manifest: LabManifest) => labManifestSchema.parse(manifest);
